import React from "react";

/**
 * Weekly opening hours editor. Saves into the business.hours dict:
 *   { monday: "09:00-12:00,14:00-19:00", tuesday: "closed", ... }
 *
 * Format per day:
 *   - "HH:MM-HH:MM"                   → 1 continuous range
 *   - "HH:MM-HH:MM,HH:MM-HH:MM"       → 2 ranges (morning + afternoon/evening,
 *                                       for shops that close for lunch)
 *   - "closed"                        → explicitly closed
 *   - "" / undefined                  → not set yet
 *
 * The comma-separated 2-range form is backward-compatible with the previous
 * single-range format (parseRanges handles both without migration).
 */
const DAYS = [
  { key: "monday", label: "Lundi" },
  { key: "tuesday", label: "Mardi" },
  { key: "wednesday", label: "Mercredi" },
  { key: "thursday", label: "Jeudi" },
  { key: "friday", label: "Vendredi" },
  { key: "saturday", label: "Samedi" },
  { key: "sunday", label: "Dimanche" },
];

const EMPTY_RANGE = { open: "", close: "" };

function parseRanges(v) {
  if (v === "closed") return { closed: true, ranges: [{ ...EMPTY_RANGE }] };
  if (!v) return { closed: false, ranges: [{ ...EMPTY_RANGE }] };
  const parts = String(v).split(",");
  const ranges = parts.slice(0, 2).map((p) => {
    const [o, c] = (p || "").split("-");
    return { open: o || "", close: c || "" };
  });
  if (ranges.length === 0) ranges.push({ ...EMPTY_RANGE });
  return { closed: false, ranges };
}

function serializeRanges(closed, ranges) {
  if (closed) return "closed";
  const nonEmpty = ranges.filter((r) => r.open || r.close);
  if (nonEmpty.length === 0) return "";
  return nonEmpty.map((r) => `${r.open || ""}-${r.close || ""}`).join(",");
}

/** Read helper exported for downstream code (SEO JSON-LD, hours-display).
 *  Returns [{open,close}, …] with only non-empty ranges, plus a `closed` flag. */
export function readHours(v) {
  const { closed, ranges } = parseRanges(v);
  return { closed, ranges: ranges.filter((r) => r.open && r.close) };
}

export default function HoursEditor({ hours, onChange }) {
  const h = hours || {};
  // Local UI state: which days have the user explicitly opened slot 2 (even
  // if empty). We store this in local component state — the storage format
  // still stays clean ("HH:MM-HH:MM" for 1 slot, "HH:MM-HH:MM,HH:MM-HH:MM"
  // for 2). This avoids polluting the DB with empty slot placeholders.
  const [openedSlot2, setOpenedSlot2] = React.useState(() => new Set());

  const update = (day, closed, ranges) => {
    onChange({ ...h, [day]: serializeRanges(closed, ranges) });
  };

  return (
    <div className="border border-border rounded-sm p-5 bg-background/50" data-testid="hours-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-display font-semibold">Horaires d&apos;ouverture</div>
          <div className="text-xs text-muted-foreground">
            Visibles sur votre fiche publique + pris en compte par Google (SEO « ouvert maintenant »).
            <br/>
            <span className="text-[11px]">Astuce : si vous fermez sur la pause déjeuner, ajoutez un 2<sup>e</sup> créneau (matin + après-midi).</span>
          </div>
        </div>
      </div>
      <div className="grid gap-3">
        {DAYS.map(({ key, label }) => {
          const { closed, ranges } = parseRanges(h[key]);
          const hasStoredSlot2 = ranges.length >= 2 && (ranges[1].open || ranges[1].close);
          const showSlot2 = hasStoredSlot2 || openedSlot2.has(key);
          const slot1 = ranges[0] || { ...EMPTY_RANGE };
          const slot2 = ranges[1] || { ...EMPTY_RANGE };

          const setSlot = (idx, patch) => {
            const nextRanges = [
              { ...slot1, ...(idx === 0 ? patch : {}) },
              { ...slot2, ...(idx === 1 ? patch : {}) },
            ];
            update(key, false, nextRanges);
          };
          const addSlot2 = () => {
            setOpenedSlot2((prev) => new Set(prev).add(key));
          };
          const removeSlot2 = () => {
            setOpenedSlot2((prev) => {
              const n = new Set(prev);
              n.delete(key);
              return n;
            });
            update(key, false, [slot1]);
          };
          const toggleClosed = (v) => update(key, v, [slot1, slot2]);

          return (
            <div key={key} className="grid grid-cols-1 md:grid-cols-[110px_auto_1fr] items-start gap-x-3 gap-y-2 border-b border-border/60 pb-3 last:border-b-0 last:pb-0" data-testid={`hours-row-${key}`}>
              <div className="text-sm font-medium pt-2">{label}</div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
                <input
                  type="checkbox"
                  checked={closed}
                  onChange={(e) => toggleClosed(e.target.checked)}
                  data-testid={`hours-${key}-closed`}
                />
                Fermé
              </label>
              <div className="grid gap-2">
                {/* Slot 1 (always visible) */}
                <div className="flex items-center gap-2" data-testid={`hours-${key}-slot-0`}>
                  <input
                    type="time"
                    value={slot1.open}
                    onChange={(e) => setSlot(0, { open: e.target.value })}
                    disabled={closed}
                    className="border border-border rounded-sm px-2 py-1.5 text-sm bg-background disabled:opacity-50 flex-1 min-w-0"
                    data-testid={`hours-${key}-open`}
                  />
                  <span className="text-xs text-muted-foreground">–</span>
                  <input
                    type="time"
                    value={slot1.close}
                    onChange={(e) => setSlot(0, { close: e.target.value })}
                    disabled={closed}
                    className="border border-border rounded-sm px-2 py-1.5 text-sm bg-background disabled:opacity-50 flex-1 min-w-0"
                    data-testid={`hours-${key}-close`}
                  />
                  {!showSlot2 && !closed && (slot1.open || slot1.close) && (
                    <button
                      type="button"
                      onClick={addSlot2}
                      className="text-[11px] text-primary hover:underline whitespace-nowrap px-1"
                      data-testid={`hours-${key}-add-slot`}
                      title="Ajouter un 2e créneau (ex: après-midi)"
                    >
                      + créneau
                    </button>
                  )}
                </div>
                {/* Slot 2 (optional — visible if used) */}
                {showSlot2 && !closed && (
                  <div className="flex items-center gap-2" data-testid={`hours-${key}-slot-1`}>
                    <input
                      type="time"
                      value={slot2.open}
                      onChange={(e) => setSlot(1, { open: e.target.value })}
                      className="border border-border rounded-sm px-2 py-1.5 text-sm bg-background flex-1 min-w-0"
                      data-testid={`hours-${key}-open-2`}
                    />
                    <span className="text-xs text-muted-foreground">–</span>
                    <input
                      type="time"
                      value={slot2.close}
                      onChange={(e) => setSlot(1, { close: e.target.value })}
                      className="border border-border rounded-sm px-2 py-1.5 text-sm bg-background flex-1 min-w-0"
                      data-testid={`hours-${key}-close-2`}
                    />
                    <button
                      type="button"
                      onClick={removeSlot2}
                      className="text-[11px] text-muted-foreground hover:text-destructive whitespace-nowrap px-1"
                      data-testid={`hours-${key}-remove-slot`}
                      title="Supprimer ce créneau"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Read-only display helper used on BusinessDetail. Returns null if no hours. */
export function HoursDisplay({ hours }) {
  if (!hours || Object.keys(hours).length === 0) return null;
  const any = DAYS.some(({ key }) => hours[key]);
  if (!any) return null;

  const todayIdx = (new Date().getDay() + 6) % 7; // Mon=0
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = (s) => {
    const [h, m] = s.split(":").map(Number);
    return h * 60 + (m || 0);
  };

  const isOpenNow = (() => {
    const todayKey = DAYS[todayIdx].key;
    const { closed, ranges } = readHours(hours[todayKey]);
    if (closed || ranges.length === 0) return false;
    return ranges.some((r) => nowMin >= toMin(r.open) && nowMin <= toMin(r.close));
  })();

  const formatRanges = (v) => {
    const { closed, ranges } = readHours(v);
    if (closed) return "Fermé";
    if (ranges.length === 0) return "—";
    return ranges.map((r) => `${r.open} – ${r.close}`).join(" · ");
  };

  return (
    <div className="border border-border rounded-sm p-5 bg-card" data-testid="hours-display">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-base font-semibold">Horaires d&apos;ouverture</div>
        <span
          className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
            isOpenNow ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          }`}
          data-testid="hours-status"
        >
          {isOpenNow ? "● Ouvert" : "○ Fermé"}
        </span>
      </div>
      <ul className="text-sm divide-y divide-border">
        {DAYS.map(({ key, label }, i) => {
          const v = hours[key];
          const isToday = i === todayIdx;
          return (
            <li key={key} className={`flex justify-between py-1.5 ${isToday ? "font-semibold" : "text-foreground/80"}`}>
              <span>{label}{isToday ? " · aujourd'hui" : ""}</span>
              <span className="tabular-nums">{!v ? "—" : formatRanges(v)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
